import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/utils';
import type { AutoWithdrawalNetworkCode } from '@shared/api/contracts';
import { validateCryptoAddress } from '@shared/lib/validate-crypto-address';
import { describeAddressFeedback } from '../../lib/address-feedback';
import { sanitizeWithdrawalAddressInput } from '../../lib/manual-form';

interface AutoWithdrawAddressHintProps {
  address: string;
  networkCode: AutoWithdrawalNetworkCode | string;
  assetCode?: string;
  fieldError?: string;
  className?: string;
  onSwitchNetwork?: (network: AutoWithdrawalNetworkCode) => void;
}

export const AutoWithdrawAddressHint = ({
  address,
  networkCode,
  assetCode = 'USDT',
  fieldError,
  className,
  onSwitchNetwork,
}: AutoWithdrawAddressHintProps) => {
  const { t } = useTranslation('cabinet');
  const [feedback, setFeedback] = useState<{ message: string; suggestedNetwork: AutoWithdrawalNetworkCode | null } | null>(
    null,
  );

  const defaultHint =
    assetCode === 'USDT'
      ? t('withdrawal.auto.single.address.valid', { network: networkCode })
      : t('withdrawal.auto.single.address.validAsset', { asset: assetCode, network: networkCode });

  useEffect(() => {
    const trimmed = sanitizeWithdrawalAddressInput(address);
    if (!trimmed) {
      setFeedback(null);
      return;
    }

    let cancelled = false;
    void validateCryptoAddress(trimmed, networkCode).then((result) => {
      if (cancelled) return;
      if (result.isValid || result.errorCode === 'REQUIRED') {
        setFeedback(null);
        return;
      }
      setFeedback(describeAddressFeedback(trimmed, networkCode, result.errorCode, t));
    });

    return () => {
      cancelled = true;
    };
  }, [address, networkCode, t]);

  const message = feedback?.message || (!address.trim() ? fieldError : undefined);
  const isError = Boolean(message);

  return (
    <p className={cn('text-xs', isError ? 'text-destructive' : 'text-muted-foreground', className)}>
      {message ?? defaultHint}
      {feedback?.suggestedNetwork && onSwitchNetwork ? (
        <>
          {' '}
          <button
            type="button"
            className="font-medium text-primary underline-offset-2 transition-colors duration-fast ease-standard hover:underline"
            onClick={() => onSwitchNetwork(feedback.suggestedNetwork as AutoWithdrawalNetworkCode)}
          >
            {t('withdrawal.validation.switchNetwork', { network: feedback.suggestedNetwork })}
          </button>
        </>
      ) : null}
    </p>
  );
};
